import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isWithinBounds,
  useGetDairyCowTypes,
  useGetFootprintingDairyHerdDetails,
  useGetFootprintingDairyHerdSize,
  useMutateFootprintingDairyHerdDetails,
  useTranslation,
} from "utils";

import { DAIRY_COW_BREED_WEIGHTS } from "consts";

import {
  DairyCowType,
  ENUM_FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION,
  ENUM_FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT,
  FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT,
  FootprintingDairyHerdSize,
} from "models";

import { Card, Flex, Form, FormInstance, InputNumber, KlimRadioGroup, PageSpin, SelectableImageCard, Typography } from "components";

interface DairyGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const COW_BODY_CONDITIONS = [
  { id: "thin", translationKey: "thin", estimatedWeight: 500 },
  { id: "moderate", translationKey: "moderate", estimatedWeight: 625 },
  { id: "fat", translationKey: "fat", estimatedWeight: 775 },
] as const;

const HerdConditionForm = ({ primaryDataId, readOnly, farmId, year }: DairyGeneralProps) => {
  const { t } = useTranslation("footprintingDairy");
  const formRef = useRef<FormInstance>(null);

  const [cowWeightKnown, setCowWeightKnown] = useState<ENUM_FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT | null>(null);
  const [averageCowWeightKg, setAverageCowWeightKg] = useState<number | null>(null);
  const [estimatedCowBodyCondition, setEstimatedCowBodyCondition] = useState<ENUM_FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION | null>(null);

  const { data: herdDetails, isLoading, isSuccess } = useGetFootprintingDairyHerdDetails(primaryDataId ?? "", { enabled: !!primaryDataId });
  const { data: herdSize } = useGetFootprintingDairyHerdSize(primaryDataId || "", { enabled: !!primaryDataId });
  const { data: cowTypes } = useGetDairyCowTypes({ enabled: true });

  const calculateWeightedAverageWeight = (herdSize: FootprintingDairyHerdSize[], cowTypes: DairyCowType[]): number => {
    const totalCows = herdSize.reduce((sum, h) => sum + h.cowCount, 0);
    if (totalCows === 0) return 0;

    const totalWeight = herdSize.reduce((sum, h) => {
      const cowType = cowTypes.find((ct) => ct.id === h.cowTypeId);
      const breedWeight = DAIRY_COW_BREED_WEIGHTS[cowType?.translationSlug as keyof typeof DAIRY_COW_BREED_WEIGHTS] || 675;
      return sum + breedWeight * h.cowCount;
    }, 0);

    return totalWeight / totalCows;
  };

  const calculatedConditionWeights = useMemo(() => {
    if (!herdSize || !cowTypes || herdSize.length === 0) {
      return COW_BODY_CONDITIONS.reduce(
        (acc, c) => {
          acc[c.id] = c.estimatedWeight;
          return acc;
        },
        {} as Record<ENUM_FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION, number>
      );
    }

    const baseWeight = calculateWeightedAverageWeight(herdSize, cowTypes);

    return {
      thin: Math.round(baseWeight * 0.9), // 10% weight reduction
      moderate: Math.round(baseWeight),
      fat: Math.round(baseWeight * 1.1), // 10% weight increase
    };
  }, [herdSize, cowTypes]);

  const { mutate: mutateHerdDetails } = useMutateFootprintingDairyHerdDetails(
    farmId,
    year,
    {
      cowWeightKnown,
      averageCowWeightKg,
      estimatedCowBodyCondition,
    },
    primaryDataId
  );

  useEffect(() => {
    if (herdDetails && isSuccess) {
      setCowWeightKnown(herdDetails.cowWeightKnown ?? null);
      setAverageCowWeightKg(herdDetails.averageCowWeightKg ?? null);
      setEstimatedCowBodyCondition(herdDetails.estimatedCowBodyCondition ?? null);

      formRef.current?.setFieldsValue({
        averageCowWeightKg: herdDetails.averageCowWeightKg ?? null,
      });

      const fieldsToValidate = (["cowWeightKnown", "averageCowWeightKg"] as const).some(
        (field) => herdDetails[field] !== null && herdDetails[field] !== undefined
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [herdDetails, isSuccess]);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateHerdDetails();
      }
    },
    [isLoading, mutateHerdDetails, readOnly]
  );

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Form ref={formRef}>
      <Flex vertical gap={16}>
        <Card border dataTestId="footprinting-dairy-herd-condition" className="padding-200">
          <Flex vertical gap={16}>
            <Flex align="center" gap={16} justify="space-between">
              <Typography variant="paragraph-m" style={{ maxWidth: "49%" }}>
                {t("dairyHerdConditionForm.question.cowWeightRadioQuestion")}
                <span className="required-asterisk">*</span>
              </Typography>
              <KlimRadioGroup
                required
                vertical={false}
                disabled={readOnly}
                type="tertiary"
                size="large"
                dataTestId="footprinting-dairy-herd-cow-weight-radio"
                onChange={({ target }) => {
                  setCowWeightKnown(target.value);
                }}
                value={cowWeightKnown}
                optionType="button"
                options={[FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT.YES, FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT.NO].map((cowWeightKnown) => ({
                  label: t(`dairyHerdConditionForm.cowWeightOptions.${cowWeightKnown}`),
                  value: cowWeightKnown,
                }))}
              />
            </Flex>

            {cowWeightKnown === FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT.YES && (
              <InputNumber
                disabled={readOnly}
                name="averageCowWeightKg"
                dataTestId="footprinting-dairy-herd-cow-weight-input"
                label={t("dairyHerdConditionForm.question.cowWeightInput")}
                suffix={t("dairyHerdConditionForm.question.cowWeightSuffix")}
                value={averageCowWeightKg}
                initialValue={averageCowWeightKg}
                onChange={(v) => setAverageCowWeightKg(v)}
                validateFirst
                rules={[
                  { required: true, message: t("dairyHerdConditionForm.error.cowWeightAmount") },
                  { type: "number" },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isWithinBounds(v, 50, 1500)) {
                        return Promise.reject(new Error(t("dairyHerdConditionForm.error.cowWeightAmount")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            )}
          </Flex>
        </Card>
        {cowWeightKnown === FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT.NO && (
          <Card border className="padding-200">
            <Flex vertical gap={16}>
              <Flex vertical className="cow-body-condition-container" gap="1rem">
                <Typography variant="h4" className="margin-bottom-100">
                  {t("dairyHerdConditionForm.estimateWeightQuestion")}
                </Typography>
                {COW_BODY_CONDITIONS.map((condition) => (
                  <SelectableImageCard
                    key={condition.id}
                    image={{
                      imageSrc: `https://klimpublic.s3.eu-central-1.amazonaws.com/cow-body-${condition.translationKey}.webp`,
                      imageAlt: `cow-body-${condition.translationKey}`,
                      imageSize: 144,
                    }}
                    selectionMode="single"
                    title={t(`dairyHerdConditionForm.bodyCondition.${condition.translationKey}.title`)}
                    description={t(`dairyHerdConditionForm.bodyCondition.${condition.translationKey}.description`)}
                    largeDescription
                    onChange={() => {
                      const conditionId = condition.id as ENUM_FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION;
                      setEstimatedCowBodyCondition(conditionId);

                      const weight = calculatedConditionWeights[conditionId];
                      setAverageCowWeightKg(weight);
                      formRef.current?.setFieldValue("averageCowWeightKg", weight);
                    }}
                    checked={estimatedCowBodyCondition === condition.id}
                    disabled={readOnly}
                  />
                ))}
              </Flex>

              <InputNumber
                disabled={readOnly}
                name="averageCowWeightKg"
                dataTestId="footprinting-dairy-herd-cow-weight-input"
                label={t("dairyHerdConditionForm.question.cowWeightInput")}
                suffix={t("dairyHerdConditionForm.question.cowWeightSuffix")}
                value={averageCowWeightKg}
                initialValue={averageCowWeightKg}
                onChange={(v) => setAverageCowWeightKg(v)}
                validateFirst
                guidanceText={t("dairyHerdConditionForm.calculationMessage")}
                rules={[
                  { required: true, message: t("dairyHerdConditionForm.error.cowWeightAmount") },
                  { type: "number" },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isWithinBounds(v, 50, 1500)) {
                        return Promise.reject(new Error(t("dairyHerdConditionForm.error.cowWeightAmount")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Flex>
          </Card>
        )}
      </Flex>
    </Form>
  );
};

HerdConditionForm.Key = "dairy.herdCondition";
export { HerdConditionForm };
